import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/constants";
import { 
  Video, Play, TrendingUp, Users, Target, Eye,
  CheckCircle, ArrowRight, Phone, Calendar, Youtube,
  Film, Clapperboard, BarChart3, Megaphone, Zap
} from "lucide-react";

const VideoMarketingPage = () => {
  const [schemas, setSchemas] = useState([]);

  useEffect(() => {
    // Set page title
    document.title = "Video Marketing DFW | Video Production Dallas | YouTube Marketing Fort Worth";
    
    // Fetch schema markup from API
    fetch(`${API}/video-marketing/schema`)
      .then(res => res.json())
      .then(data => setSchemas(data))
      .catch(() => {});
  }, []);

  const stats = [
    { value: "Strategy", label: "Channel & Content Plans", icon: TrendingUp },
    { value: "Production", label: "Short + Long-Form Video", icon: Film },
    { value: "YouTube", label: "Channel Management", icon: Eye },
    { value: "Video SEO", label: "Search-Optimized Video", icon: Target }
  ];

  const services = [
    { icon: Youtube, title: "YouTube Marketing", desc: "Channel optimization, SEO, and growth strategies for Dallas businesses on YouTube." },
    { icon: Video, title: "Video Production", desc: "Professional video content from concept to delivery for DFW companies." },
    { icon: Clapperboard, title: "Short-Form Content", desc: "TikTok, Reels, and Shorts optimized for maximum local reach." },
    { icon: Megaphone, title: "Video Advertising", desc: "YouTube Ads, social video ads targeting Dallas-Fort Worth audiences." },
    { icon: BarChart3, title: "Video SEO", desc: "Rank your videos in Google, YouTube, and AI search results." },
    { icon: Zap, title: "Live Streaming", desc: "Professional live event streaming and webinar production." }
  ];

  const videoTypes = [
    "Brand Videos", "Product Demos", "Customer Testimonials", "Explainer Videos",
    "Social Media Content", "Training Videos", "Event Coverage", "Drone Footage"
  ];

  const dfwAreas = [
    "Dallas", "Fort Worth", "Plano", "Irving", "Arlington", 
    "Frisco", "McKinney", "Denton", "Richardson", "Garland"
  ];

  const faqs = [
    { 
      q: "Why is video marketing important for Dallas businesses?", 
      a: "Video content generates 1200% more shares than text and images combined. For DFW businesses, local video marketing helps you connect with the 7.5 million people in the metroplex, build brand recognition, and drive foot traffic to your location." 
    },
    { 
      q: "What types of videos work best for local businesses?",
      a: "Customer testimonials, behind-the-scenes content, local event coverage, and 'day in the life' videos perform exceptionally well for Dallas businesses. Short-form vertical videos (under 60 seconds) get 2.5x more engagement on platforms like TikTok and Instagram."
    },
    { 
      q: "How much does video production cost in Dallas?",
      a: "Professional video production in Dallas ranges from $1,500 for simple social content to $15,000+ for full commercial production. We offer packages starting at $2,500/month that include strategy, production, and distribution for consistent content."
    },
    { 
      q: "How do you optimize videos for local search in DFW?",
      a: "We implement local video SEO including geo-tagged descriptions, Dallas-specific keywords, local schema markup, Google Business Profile video optimization, and strategic posting times for Central Time Zone audiences."
    }
  ];


  return (
    <div className="min-h-screen bg-black" data-testid="video-marketing-page">
      <Helmet>
        <title>Video Marketing DFW | Video Production Dallas | YouTube Marketing Fort Worth</title>
        <meta name="description" content="Dallas video marketing agency. Professional video production, YouTube channel management, TikTok marketing, social video content & video SEO for DFW businesses. Free video strategy session!" />
        <meta name="keywords" content="Video marketing DFW, video production Dallas, YouTube marketing Dallas, TikTok marketing Fort Worth, social media video Dallas, video SEO Texas, corporate video production DFW, video advertising Dallas" />
        <link rel="canonical" href="https://digitalmedianinja.com/video-marketing" />
        <meta property="og:title" content="Video Marketing DFW | Video Production & YouTube Marketing Dallas" />
        <meta property="og:description" content="Dallas video marketing agency. Professional video production, YouTube optimization, TikTok content creation, and social video marketing for DFW businesses." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1634812932028-3baa37d90b52?w=1200&h=630&fit=crop" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Professional Video Production in Dallas - Video Marketing Services" />
        <meta property="og:url" content="https://digitalmedianinja.com/video-marketing" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Digital Media Ninja" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Video Marketing DFW | Video Production Dallas" />
        <meta name="twitter:description" content="Professional video marketing services in Dallas-Fort Worth. YouTube, TikTok, and social video content." />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1634812932028-3baa37d90b52?w=1200&h=630&fit=crop" />
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content="Dallas" />
        {schemas.map((schema, idx) => (
          <script key={idx} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      {/* Hero with Video Background */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover opacity-30"
          >
            <source src="https://customer-assets.emergentagent.com/job_seo-blog-boost/artifacts/z6d55k3y_Shorter%20Reel.mov" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/60"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 via-transparent to-purple-500/10 z-[1]"></div>
        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/30 rounded-full mb-6">
                <Video className="w-4 h-4 text-red-400" />
                <span className="text-red-400 text-sm font-medium">Dallas Video Marketing Experts</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                <span className="text-red-400">Video Marketing</span> That Drives Results in Dallas
              </h1>
              
              <p className="text-lg text-gray-300 mb-8 max-w-2xl">
                Professional video production and marketing for Dallas-Fort Worth businesses. From YouTube optimization to TikTok content, we help DFW brands tell their story and convert viewers into customers.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <Button 
                  size="lg" 
                  className="bg-red-500 hover:bg-red-600 text-white font-semibold" 
                  onClick={() => window.location.href='/contact'}
                  data-testid="video-cta-hero"
                >
                  Get Free Video Strategy Guide <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
                    <Phone className="w-5 h-5 mr-2" /> Schedule Discovery Call
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {stats.map((stat, i) => (
                  <div key={i} className="text-center px-3 py-4 bg-white/5 rounded-xl border border-white/10 backdrop-blur-sm flex flex-col items-center justify-center min-h-[110px]">
                    <stat.icon className="w-6 h-6 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{stat.value}</p>
                    <p className="text-xs text-gray-400">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Featured Video Showcase */}
            <div className="relative">
              <div className="aspect-video rounded-2xl overflow-hidden border border-white/20 shadow-2xl shadow-red-500/20">
                <video
                  controls
                  className="w-full h-full object-cover bg-black"
                  poster="/posters/hollywood_reel_poster.jpg"
                >
                  <source src="https://customer-assets.emergentagent.com/job_seo-blog-boost/artifacts/z6d55k3y_Shorter%20Reel.mov" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="absolute -top-4 -right-4 bg-red-500 text-white px-4 py-2 rounded-lg font-semibold text-sm shadow-lg">
                Featured Work
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DFW Service Areas */}
      <section className="py-12 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <p className="text-center text-gray-400 mb-6">Serving businesses across the Dallas-Fort Worth Metroplex</p>
          <div className="flex flex-wrap justify-center gap-3">
            {dfwAreas.map((area, i) => (
              <span key={i} className="px-4 py-2 bg-white/5 rounded-full text-sm text-gray-300 border border-white/10">
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Video Marketing Services for DFW</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Full-service video marketing tailored for Dallas-Fort Worth businesses.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <Card key={i} className="bg-[#111] border-white/10 hover:border-red-500/30 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                    <svc.icon className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{svc.title}</h3>
                  <p className="text-gray-400 text-sm">{svc.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Types */}
      <section className="py-20 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Types of Videos We Create</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {videoTypes.map((type, i) => (
              <div key={i} className="p-4 bg-white/5 rounded-xl border border-white/10 text-center hover:border-red-500/30 transition-colors">
                <Play className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-white font-medium">{type}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20" data-testid="video-pricing-section">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-4">Video Production Pricing</h2>
          <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
            Transparent pricing for professional video production in Dallas-Fort Worth. All packages include pre-production planning, professional filming, and color-graded deliverables.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Social Media Package */}
            <Card className="bg-[#111] border-white/10 hover:border-red-500/30 transition-all relative overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">Social Media Package</h3>
                  <p className="text-gray-400 text-sm">Perfect for ongoing social content</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">$1,000</span>
                  <span className="text-gray-400 ml-2">/ session</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>2-3 hour film session</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>3 edited reels (vertical format)</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Color-graded raw clips included</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>TikTok, Reels & Shorts optimized</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Ideal for fitness, lifestyle, retail</span>
                  </li>
                </ul>
                <Link to="/contact">
                  <Button className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20">
                    Get Started
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Half-Day Rate - Most Popular */}
            <Card className="bg-[#111] border-red-500/50 hover:border-red-500 transition-all relative overflow-hidden scale-105">
              <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-2 text-sm font-semibold">
                MOST POPULAR
              </div>
              <CardContent className="p-8 pt-14">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">Half-Day Production</h3>
                  <p className="text-gray-400 text-sm">Events & promotional content</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">$2,500</span>
                  <span className="text-gray-400 ml-2">/ half day</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Up to 4 hours on location</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Multiple video deliverables</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Professional editing & color grading</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Event coverage (galas, launches, etc.)</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Both horizontal & vertical formats</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Quick 5-7 day turnaround</span>
                  </li>
                </ul>
                <Link to="/contact">
                  <Button className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold">
                    Book Now <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Full-Day Rate */}
            <Card className="bg-[#111] border-white/10 hover:border-red-500/30 transition-all relative overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-white mb-2">Full-Day Production</h3>
                  <p className="text-gray-400 text-sm">Complete event & commercial coverage</p>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-white">$4,000</span>
                  <span className="text-gray-400 ml-2">/ full day</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>6-8 hours on location</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Full event documentation</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Premium editing & motion graphics</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Perfect for tournaments, conferences</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>Highlight reel + full coverage</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-300 text-sm">
                    <CheckCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <span>All raw footage included</span>
                  </li>
                </ul>
                <Link to="/contact">
                  <Button className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20">
                    Get Started
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-gray-500 text-sm mt-8">
            * Custom packages available for ongoing retainers and multi-day productions. <Link to="/contact" className="text-red-400 hover:underline">Contact us</Link> for enterprise pricing.
          </p>
        </div>
      </section>

      {/* Why Video for DFW */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">Why Video Marketing Matters for Dallas Businesses</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300"><strong className="text-white">86% of businesses</strong> use video as a marketing tool</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300"><strong className="text-white">Video on landing pages</strong> increases conversions by 80%</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300"><strong className="text-white">Viewers retain 95%</strong> of a message in video vs 10% in text</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300"><strong className="text-white">YouTube is the #2 search engine</strong> — your customers are there</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300"><strong className="text-white">Short-form video</strong> has 2.5x higher engagement than other content</p>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300"><strong className="text-white">Local video content</strong> builds trust with DFW audiences</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Video Marketing FAQs for Dallas Businesses</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="group bg-[#111] rounded-xl border border-white/10" data-testid={`video-faq-${i}`}>
                <summary className="p-6 cursor-pointer text-white font-medium flex items-center justify-between hover:bg-white/5">
                  {faq.q}
                  <span className="text-red-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="px-6 pb-6 text-gray-400">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-red-500/10 to-purple-500/10">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Ready to Dominate Video in Dallas?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Get a free video marketing strategy session and discover how to reach more DFW customers with video.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-red-500 hover:bg-red-600 text-white font-semibold">
                <Calendar className="w-5 h-5 mr-2" /> Book Strategy Session
              </Button>
            </Link>
            <Link to="/hyrox-content">
              <Button size="lg" variant="outline" className="border-yellow-400/30 text-yellow-400 hover:bg-yellow-400/10">
                HYROX Cinematic Content
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/20 text-white" 
              onClick={() => window.location.href='/contact'}
              data-testid="video-cta-bottom"
            >
              Download Free Video Guide
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VideoMarketingPage;
